import { useEffect, useRef } from "react";
import ChartCard from "../../../components/ui/ChartCard";
import InfoTooltip from "../../../components/ui/InfoTooltip";
import {
  usePdfExport,
  usePdfExportSection,
} from "../../../context/PdfExportContext";
import captureElementForPdf from "../../../utils/captureElementForPdf";
import { LOONOPZAND_PERIOD_COLORS } from "./chartOptions";

function formatPercentage(value) {
  const number = Number(value) || 0;
  return Number.isInteger(number) ? `${number}%` : `${number.toFixed(1)}%`;
}

function formatDutchAverage(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return "–";
  }
  return Number(value).toFixed(1).replace(".", ",");
}

export default function LoonOpZandScorePanel({ question, filters, mode, info }) {
  const averageRef = useRef(null);
  const pdfExport = usePdfExport();
  const pdfSection = usePdfExportSection();
  const issues = filters?.selected_issues || [];
  const issueOptions = new Map(
    (filters?.available_issues || []).map((issue) => [issue.value, issue])
  );
  const issueStats = new Map(
    (question?.stats?.by_issue || []).map((item) => [item.issue, item])
  );
  const scoreOptions = Array.from({ length: 10 }, (_item, index) => {
    const score = index + 1;
    return (question?.options || []).find(
      (option) => Number(option.score ?? option.code) === score
    );
  });
  const details = issues.map((issue) =>
    scoreOptions.map((option) => {
      const value = option?.by_issue?.find((item) => item.issue === issue);
      return {
        count: Number(value?.count) || 0,
        percentage: Number(value?.percentage) || 0,
      };
    })
  );
  const series = issues.map((issue, seriesIndex) => ({
    name: issueOptions.get(issue)?.label || issue,
    data: details[seriesIndex].map((item) =>
      mode === "rel" ? item.percentage : item.count
    ),
  }));
  const maximum = Math.max(
    0,
    ...series.flatMap((item) => item.data.map((value) => Number(value) || 0))
  );
  const axisMaximum = mode === "rel"
    ? Math.min(100, Math.max(10, Math.ceil(maximum + 10)))
    : Math.max(1, Math.ceil(maximum * 1.25));
  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: LOONOPZAND_PERIOD_COLORS,
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 3,
        columnWidth: "76%",
        dataLabels: { position: "top" },
      },
    },
    xaxis: {
      categories: Array.from({ length: 10 }, (_item, index) => String(index + 1)),
      title: { text: "Rapportcijfer" },
      labels: { style: { fontSize: "12px" } },
    },
    yaxis: {
      min: 0,
      max: axisMaximum,
      forceNiceScale: true,
      labels: {
        formatter: (value) =>
          mode === "rel" ? `${Math.round(Number(value))}%` : `${Math.round(Number(value))}`,
      },
    },
    legend: { position: "bottom", fontSize: "13px" },
    grid: { padding: { top: 28, left: 8, right: 12 } },
    dataLabels: {
      enabled: true,
      offsetY: -12,
      formatter: (_value, context) => {
        const detail = details?.[context.seriesIndex]?.[context.dataPointIndex];
        if (!detail?.count) {
          return "";
        }
        return `${detail.count}  ${formatPercentage(detail.percentage)}`;
      },
      style: {
        fontSize: "9px",
        fontWeight: 600,
        colors: ["#374151"],
      },
      background: { enabled: false },
    },
    tooltip: {
      y: {
        formatter: (_value, context) => {
          const detail = details?.[context.seriesIndex]?.[context.dataPointIndex];
          const score = context.dataPointIndex + 1;
          return detail
            ? `Rapportcijfer ${score} · ${detail.count} respondenten · ${formatPercentage(detail.percentage)}`
            : "";
        },
      },
    },
  };

  useEffect(() => {
    if (!pdfSection?.sectionKey || !pdfExport?.registerExportable) {
      return undefined;
    }
    return pdfExport.registerExportable({
      sectionKey: pdfSection.sectionKey,
      order: (pdfSection.order || 0) - 10,
      getContent: async () => {
        if (!averageRef.current) {
          return null;
        }
        const canvas = await captureElementForPdf(averageRef.current);
        return {
          image: {
            src: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          },
        };
      },
    });
  }, [pdfExport, pdfSection]);

  return (
    <div className="loonopzand-dashboard__score-panel">
      <section
        ref={averageRef}
        className="chart-card loonopzand-dashboard__score-average-card"
      >
        <header className="chart-card__header">
          <div>
            <div className="chart-title-with-info">
              <h3 className="chart-title">{question.text}</h3>
              {info && <InfoTooltip text={info} label={`Informatie over ${question.text}`} />}
            </div>
            <p className="loonopzand-dashboard__score-subtitle">
              Gemiddeld rapportcijfer per periode
            </p>
          </div>
        </header>
        <div className="loonopzand-dashboard__score-averages">
          {issues.map((issue, index) => {
            const stats = issueStats.get(issue);
            return (
              <div
                key={issue}
                className="loonopzand-dashboard__score-average"
                style={{ "--score-period-color": LOONOPZAND_PERIOD_COLORS[index % LOONOPZAND_PERIOD_COLORS.length] }}
              >
                <span>{issueOptions.get(issue)?.label || issue}</span>
                <strong>{formatDutchAverage(stats?.average)}</strong>
                <small>{stats?.answered_count || 0} antwoorden</small>
              </div>
            );
          })}
        </div>
      </section>

      <ChartCard
        chartKey={`loonopzand-score-${mode}-${issues.join("-")}`}
        title="Rapportcijferverdeling"
        info={info || "De percentages zijn per periode berekend over de respondenten met een geldig rapportcijfer."}
        type="bar"
        options={options}
        series={series}
        height={460}
      />
    </div>
  );
}
